import { useRef } from 'react';
import {
  Box, Typography, Chip,
} from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import { Add, Edit, VisibilityOff } from '@mui/icons-material';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid-premium';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useServerPagination } from '../../hooks/useServerPagination';
import { useGridHeight } from '../../hooks/useGridHeight';
import { partnersService } from '../../services/partners.service';
import { useModalState } from '../../hooks/useModalState';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useDebounced } from '../../hooks/useDebounced';
import EntityGridToolbar from '../../components/EntityGridToolbar';
import CreateEditPartner from './CreateEditPartner';

export default function PartnersPage() {
  const queryClient = useQueryClient();
  const { setModal } = useModalState('partner');
  const [search, setSearch] = useUrlState('q', '', codecs.str);
  const [statuses, setStatuses] = useUrlState<string[]>('status', ['active'], codecs.statusCsv);

  const filterRef = useRef<HTMLDivElement>(null);
  const gridHeight = useGridHeight(filterRef);
  const debouncedSearch = useDebounced(search);
  const { gridProps, offset, limit } = useServerPagination(50, `${debouncedSearch}|${statuses.join(',')}`);

  const { data, isFetching } = useQuery({
    queryKey: ['partners', offset, limit, debouncedSearch, statuses],
    queryFn: () => partnersService.query({
      offset, limit, search: debouncedSearch,
      // partners is the one entity storing status lowercase (see #184); everything
      // above this line uses the same uppercase values as every other page.
      statuses: statuses.map((s) => s.toLowerCase()),
    }),
    placeholderData: (prev) => prev,
  });

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    {
      field: 'api_key', headerName: 'API Key', width: 180,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem', display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value ? `${String(p.value).substring(0, 12)}...` : '-'}
        </Box>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={p.value}
            size="small"
            color={p.value === 'active' ? 'success' : p.value === 'archived' ? 'warning' : 'default'}
          />
        </Box>
      ),
    },
    {
      field: 'created_at', headerName: 'Created', width: 110,
      valueFormatter: (v: string) => new Date(v).toLocaleDateString(),
    },
    {
      field: 'actions', headerName: '', width: 70, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0, alignItems: 'center', height: '100%' }}>
          <TooltipIconButton size="small" onClick={() => setModal({ open: true, id: params.row.id, reason: 'Update' })} title="Edit">
            <Edit sx={{ fontSize: 16 }} />
          </TooltipIconButton>
          <TooltipIconButton
            size="small"
            title="Archive"
            onClick={async () => {
              await partnersService.delete(params.row.id);
              queryClient.invalidateQueries({ queryKey: ['partners'] });
            }}
          >
            <VisibilityOff sx={{ fontSize: 16 }} />
          </TooltipIconButton>
        </Box>
      ),
    },
  ];

  const rows = data?.data || [];

  return (
    <Box>
      <Box ref={filterRef} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>Partners</Typography>
      </Box>

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isFetching}
        {...gridProps(data?.total)}
        showToolbar
        slots={{ toolbar: EntityGridToolbar }}
        slotProps={{ toolbar: {
          search, onSearch: setSearch,
          statuses: { value: statuses, onChange: setStatuses },
          actions: [{ icon: Add, hint: 'Add partner', onClick: () => setModal({ open: true, reason: 'Create' }) }],
        } }}
        disableRowSelectionOnClick
        onRowDoubleClick={(params: GridRowParams, event: React.MouseEvent) => {
          const target = event.target as HTMLElement;
          if (target.closest('[data-field="actions"]')) return;
          setModal({ open: true, id: params.row.id, reason: 'Update' });
        }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2, height: gridHeight }}
      />

      <CreateEditPartner />
    </Box>
  );
}
